import { CheckCircle2, Loader2, CircleX } from 'lucide-react'
import { useSpinDelay } from 'spin-delay'
import { cn } from '#app/utils/misc.tsx'
import { Button, type ButtonVariant } from './button.tsx'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from './tooltip.tsx'

export const StatusButton = ({
	message,
	status,
	className,
	children,
	spinDelay,
	...props
}: React.ComponentProps<'button'> &
	ButtonVariant & {
		status: 'pending' | 'success' | 'error' | 'idle'
		message?: string | null
		spinDelay?: Parameters<typeof useSpinDelay>[1]
	}) => {
	const delayedPending = useSpinDelay(status === 'pending', {
		delay: 400,
		minDuration: 300,
		...spinDelay,
	})

	const companion = {
		pending: delayedPending ? (
			<div
				role="status"
				className="inline-flex items-center justify-center"
				aria-label="Loading"
			>
				<Loader2
					className="text-muted-foreground h-4 w-4 animate-spin"
					aria-hidden="true"
				/>
			</div>
		) : null,
		success: (
			<div
				role="status"
				className="inline-flex items-center justify-center"
				aria-label="Success"
			>
				<CheckCircle2
					className="h-4 w-4 text-emerald-600 dark:text-emerald-400"
					aria-hidden="true"
				/>
			</div>
		),
		error: (
			<div
				role="status"
				className="inline-flex items-center justify-center"
				aria-label="Error"
			>
				<CircleX className="text-destructive h-4 w-4" aria-hidden="true" />
			</div>
		),
		idle: null,
	}[status]

	return (
		<Button
			className={cn(
				'inline-flex items-center justify-center gap-2',
				status === 'pending' && delayedPending && 'cursor-wait',
				className,
			)}
			disabled={status === 'pending' || props.disabled}
			{...props}
		>
			{message ? (
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger asChild>
							<span className="inline-flex items-center">{companion}</span>
						</TooltipTrigger>
						<TooltipContent side="top">
							<p>{message}</p>
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			) : (
				companion
			)}
			<span className="flex items-center gap-2 truncate">{children}</span>
		</Button>
	)
}

StatusButton.displayName = 'StatusButton'
